import React, { useState } from 'react';
import { Grid3X3, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useLanguage } from '@/contexts/LanguageContext';

interface TemplateGeneratorProps {
  onGenerateTemplate: (rows: number, cols: number) => void;
}

const TemplateGenerator: React.FC<TemplateGeneratorProps> = ({ onGenerateTemplate }) => {
  const { t } = useLanguage();
  const [rows, setRows] = useState(2);
  const [cols, setCols] = useState(2);
  const [open, setOpen] = useState(false);

  const handleGenerate = () => {
    onGenerateTemplate(rows, cols);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-full">
          <Grid3X3 className="w-4 h-4 mr-2" />
          {t.createTemplate}
        </Button>
      </DialogTrigger>
      <DialogContent className="glass border-border">
        <DialogHeader>
          <DialogTitle className="text-foreground">{t.templateGenerator}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="panel-label text-muted-foreground">{t.rows}</Label>
              <span className="panel-value font-medium text-primary">{rows}</span>
            </div>
            <Slider
              value={[rows]}
              onValueChange={([v]) => setRows(v)}
              min={1}
              max={5}
              step={1}
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="panel-label text-muted-foreground">{t.columns}</Label>
              <span className="panel-value font-medium text-primary">{cols}</span>
            </div>
            <Slider
              value={[cols]}
              onValueChange={([v]) => setCols(v)}
              min={1}
              max={5}
              step={1}
            />
          </div>

          {/* Preview */}
          <div className="aspect-square max-w-[200px] mx-auto rounded-lg overflow-hidden bg-muted p-2">
            <div
              className="w-full h-full grid gap-1"
              style={{
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gridTemplateRows: `repeat(${rows}, 1fr)`,
              }}
            >
              {Array.from({ length: rows * cols }).map((_, i) => (
                <div key={i} className="bg-primary/30 rounded-sm" />
              ))}
            </div>
          </div>

          <p className="panel-label text-muted-foreground text-center">
            {rows * cols} {t.cellsWillBeCreated}
          </p>

          <Button onClick={handleGenerate} className="w-full" variant="neon">
            <Plus className="w-4 h-4 mr-2" />
            {t.applyTemplate}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateGenerator;